import React, { SyntheticEvent, useEffect, useState } from 'react';
import { Backdrop, CircularProgress } from '@mui/material';
import { AxiosError } from 'axios';
import { useSnackbar } from 'notistack';
import {
  getLocations,
  getPrograms,
  getSectionById,
  getTeachers,
  putUpdateSection,
} from '../../../../requests/api/sections';
import { getSubjects } from '../../../../requests/api/subjects';
import handleApiResponse from '../../../../utils/handleApiResponse';
import { useYear } from '../../../../hooks/useYear';
import { ProgramData, SubjectData } from '../../../Program/ProgramListView/types';
import {
  CustomSectionData,
  LocationsData,
  SECTIONS_UI,
  SectionData,
  Teacher,
} from '../types';
import UpsertSectionModal from './UpsertSectionModal';

const EMPTY_MODAL_VALUES: SectionData = {
  id: '',
  name: '',
  subjectId: '',
  programId: '',
  locationId: '',
  classroom: '',
  studentsCapacity: 0,
  mipreuId: undefined,
  timeSlotIds: [],
  teacherInfo: [],
};

/**
 * Modal de edición de una sección que se abre desde cualquier vista teniendo
 * solo su id: trae la sección y las opciones de los selectores, y reutiliza el
 * mismo UpsertSectionModal del listado de secciones.
 */
function EditSectionByIdModal({
  sectionId,
  onClose,
  onUpdated,
}: {
  sectionId: string | null,
  onClose: () => void,
  onUpdated: () => void,
}) {
  const { enqueueSnackbar } = useSnackbar();
  const { selectedYear } = useYear();

  // Copia propia de la UI: SECTIONS_UI se muta al cargar los selectFields.
  const [sectionsUI, setSectionsUI] = useState(() => SECTIONS_UI.map((elem) => ({ ...elem })));
  const [section, setSection] = useState<CustomSectionData | null>(null);
  const [modalAddEdit, setModalAddEdit] = useState({ id: '', open: false });
  const [modalValues, setModalValues] = useState<SectionData>(EMPTY_MODAL_VALUES);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!sectionId) {
      setSection(null);
      setModalAddEdit({ id: '', open: false });
      setModalValues(EMPTY_MODAL_VALUES);
      return;
    }

    const fetchSectionAndOptions = async () => {
      setLoading(true);
      try {
        const [
          { section: sectionInfo },
          { subjects },
          { locations },
          { teachers },
        ] = await Promise.all([
          getSectionById(sectionId),
          getSubjects(),
          getLocations(),
          getTeachers(),
        ]);

        const apiSection = sectionInfo as SectionData;

        setSectionsUI((prevState) => prevState.map((elem) => {
          if (elem.label === 'Asignatura') {
            return { ...elem, selectFields: subjects as SubjectData[] };
          }
          if (elem.label === 'Sede') {
            return { ...elem, selectFields: locations as LocationsData[] };
          }
          if (elem.label === 'Profesor') {
            return { ...elem, selectFields: teachers as Teacher[] };
          }
          return elem;
        }));

        setSection({
          ...apiSection,
          id: `${apiSection.id}`,
          sectionTimeSlots: (apiSection.sectionTimeSlots ?? [])
            .map((elem) => ({ ...elem, sectionId: `${elem.sectionId}` })),
          subjectId: apiSection.program?.subjectId ?? apiSection.program?.subject.id ?? '',
          subjectName: apiSection.program?.subject.name ?? '',
          programName: apiSection.program?.name ?? '',
          locationName: apiSection.location?.name ?? '',
          teacherName: '',
        });
        setModalAddEdit({ id: `${apiSection.id}`, open: true });
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
        onClose();
      } finally {
        setLoading(false);
      }
    };

    fetchSectionAndOptions();
  }, [sectionId]);

  useEffect(() => {
    if (!modalAddEdit.open) {
      return;
    }

    const fetchProgramsForSubject = async () => {
      try {
        const { programs } = await getPrograms(modalValues.subjectId, selectedYear);
        setSectionsUI((prevState) => prevState.map((elem) => (elem.label === 'Programa'
          ? { ...elem, selectFields: programs as ProgramData[] }
          : elem)));
      } catch (err) {
        const e = err as AxiosError;
        handleApiResponse(enqueueSnackbar, e, false);
      }
    };

    fetchProgramsForSubject();
  }, [modalAddEdit.open, modalValues.subjectId, selectedYear]);

  const handleCloseModal = () => {
    onClose();
  };

  const handleFieldChange = (
    e: React.ChangeEvent<HTMLInputElement> | SyntheticEvent<Element, Event> | Date | File,
    source: string,
  ) => {
    if (e instanceof Date) {
      setModalValues((prevState) => ({ ...prevState, [source]: e }));
    } else if (e instanceof File) {
      // No use of file here. Just type handling
    } else if (source === 'studentsCapacity') {
      const { value } = e.target as HTMLInputElement;
      const numValue = value === '' ? 0 : Number(value);
      setModalValues((prevState) => ({ ...prevState, [source]: numValue }));
    } else {
      setModalValues(
        (prevState) => ({ ...prevState, [source]: (e.target as HTMLInputElement).value }),
      );
    }
  };

  const editASection = async (id: string) => {
    if (modalValues.studentsCapacity <= 0) {
      enqueueSnackbar('El cupo mínimo de estudiantes debe ser mayor a 0', { variant: 'error' });
      return;
    }

    try {
      const response = await putUpdateSection({
        id,
        programId: modalValues.programId,
        locationId: modalValues.locationId,
        classroom: modalValues.classroom,
        studentsCapacity: modalValues.studentsCapacity,
        timeSlotIds: modalValues.timeSlotIds,
        teacherInfo: modalValues.teacherInfo,
        mipreuId: modalValues.mipreuId,
      });
      handleApiResponse(enqueueSnackbar, response, true);
      onUpdated();
    } catch (err) {
      const e = err as AxiosError;
      handleApiResponse(enqueueSnackbar, e, false);
    }
  };

  return (
    <>
      <Backdrop open={loading} sx={{ zIndex: (theme) => theme.zIndex.modal + 1 }}>
        <CircularProgress color='inherit' />
      </Backdrop>
      {section && <UpsertSectionModal
        allItems={{ items: [section], totalItems: 1 }}
        modalAddEdit={modalAddEdit}
        setModalAddEdit={setModalAddEdit}
        handleCloseModal={handleCloseModal}
        modalValues={modalValues}
        setModalValues={setModalValues}
        handleFieldChange={handleFieldChange}
        itemsUI={sectionsUI}
        addFunction={handleCloseModal}
        editFunction={editASection}
      />}
    </>
  );
}

export default EditSectionByIdModal;
